import axios from 'axios';
import { API_URL } from '@/common/config';
import JwtService from '../common/jwt.service.js';
import { useToast } from 'vue-toast-notification';
import { userStore } from "@/store/modules/user.js";
import requestLogger from '@/utils/requestLogger';
import { createErrorNotificationLimiter } from '@/utils/errorNotificationLimiter';

const $toast = useToast();
const limitErrorNotification = createErrorNotificationLimiter();

const showErrorNotification = (status, message) => {
    limitErrorNotification(status, () => {
        $toast.error(message, {
            position: 'top-right'
        });
    });
};

export class ValidationError extends Error {
    constructor(responseOrData) {
        // текст ошибки бэкенда, чтобы формы могли показать его у поля
        super(responseOrData?.data?.message ?? responseOrData?.message ?? '');
        const errors =
            responseOrData?.data?.errors ??
            responseOrData?.errors ??
            {};
        this.errors = errors;
    }

    hasError(key) {
        return !!this.errors[key];
    }

    getError(key) {
        return this.hasError(key) ? this.errors[key][0] : undefined;
    }

    getErrors() {
        return Object.keys(this.errors).reduce(
            (agg, key) => ({ ...agg, [key]: this.errors[key] }),
            {}
        );
    }
}

const apiToken = JwtService.getToken();

const axiosInstance = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Authorization: apiToken ? `Bearer ${apiToken}` : null
    }
});

axiosInstance.interceptors.request.use(
    (config) => {
        const UserStore = userStore();
        const timezone = UserStore.getTimezoneKey;
        if (timezone && config.data) {
            // Используем 3d-party api для support
            // Поэтому не надо отправлять timezone
            const isSupportUrl = config.url.split('/').includes('helpdesk');
            const isPointUrl = config.url.split('/').includes('point');
            const skipTimezone = config.skipTimezone || isSupportUrl || isPointUrl;

            if (['post', 'put', 'get', 'delete', 'patch'].includes(config.method) && !skipTimezone) {
                config.data.timezone_key = timezone;
            }
        }
        return new Promise((resolve, reject) => {
            requestLogger.logRequest(config, resolve, reject);
            resolve(config);
        });
    },
    (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.config?.skipGlobalErrorHandler) {
            if (error.response?.status === 401 && !error.config?.skipAuthRedirect) {
                showErrorNotification(401, error.response.data.message);
                JwtService.unsetToken('auth');
                window.location.href = '/auth/login';
                return;
            }
            return Promise.reject(error);
        }

        if (error.response) {
            if (error.response.status === 422) {
                if (error.response.data?.message) {
                    showErrorNotification(422, error.response.data.message);
                } else {
                    limitErrorNotification(422, () => {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                            const jsonData = JSON.parse(event?.target?.result);
                            $toast.error(jsonData?.message, {
                                position: 'top-right'
                            });
                        };
                        reader.readAsText(error.response.data);
                    });
                }
                return Promise.reject(new ValidationError(error.response));
            }

            if (error.response.status === 403 && (error.response.data.code === 3 || error.response.data.code === 4030)) {
                showErrorNotification(403, error.response.data.message || 'Error');
            }

            if (error.response.status === 401 && !error.config?.skipAuthRedirect) {
                showErrorNotification(401, error.response.data.message);
                JwtService.unsetToken('auth');
                window.location.href = '/auth/login';
                return;
            }

            if (
                error.response.status === 500 ||
                error.response.status === 429 ||
                error.response.status === 400
            ) {
                showErrorNotification(
                    error.response.status,
                    error.response.data?.message || error.response.data.error?.message || 'Error'
                );
                throw new ValidationError(error.response);
            }

            if (error.response.status === 502) {
                showErrorNotification(502, error.message);
            }
        }
        throw error;
    }
);

export default axiosInstance;
